<template>
    <ContentLayout>
        <PageTitle title="Visi kontaktai" />

        <Card>
            <template #header>
                <div class="pl-5 pt-4 font-medium text-[16px]">Filtrai</div>
            </template>
            <template #content>
                <div class="flex flex-row flex-wrap gap-2">
                    <FloatLabel variant="on" class="w-full max-w-[400px]">
                        <InputText id="over_label" v-model="search" class="w-full" size="small" />
                        <label for="over_label" class="!font-normal">Paieška</label>
                    </FloatLabel>

                    <FloatLabel variant="on" class="w-full max-w-[400px]">
                        <MultiSelect
                            v-model="filters.stages"
                            :maxSelectedLabels="4"
                            :options="availableStages"
                            optionLabel="name"
                            optionValue="code"
                            class="w-full"
                            @change="filter"
                            size="small"
                        >
                            <template #option="slotProps">
                                <div class="flex items-center">
                                    <div>{{ slotProps.option.name }}</div>
                                </div>
                            </template>
                        </MultiSelect>
                        <label for="over_label" class="!font-normal">Stadija</label>
                    </FloatLabel>

                    <FloatLabel variant="on" class="w-full max-w-[400px]">
                        <MultiSelect
                            v-model="filters.special_attributes"
                            :options="availableSpecialAttributes"
                            optionLabel="name"
                            optionValue="code"
                            class="w-full"
                            @change="filter"
                            size="small"
                        >
                            <template #option="slotProps">
                                <div class="flex items-center">
                                    <img
                                        :src="
                                            './assets/images/icons/' +
                                            slotProps.option.icon +
                                            '.svg'
                                        "
                                        class="w-5 h-5 mr-2"
                                    />
                                    <div>{{ slotProps.option.name }}</div>
                                </div>
                            </template>
                        </MultiSelect>
                        <label for="over_label" class="!font-normal">Statusas</label>
                    </FloatLabel>

                    <FloatLabel variant="on" class="w-full max-w-[400px]">
                        <MultiSelect
                            v-model="filters.regions"
                            :options="availableRegions"
                            optionLabel="name"
                            optionValue="code"
                            class="w-full"
                            @change="filter"
                            size="small"
                        >
                            <template #option="slotProps">
                                <div class="flex items-center">
                                    <div>{{ slotProps.option.name }}</div>
                                </div>
                            </template>
                        </MultiSelect>
                        <label for="over_label" class="!font-normal">Regionas</label>
                    </FloatLabel>

                    <div class="w-full max-w-[400px]">
                        <div class="flex flex-row gap-2">
                            <InputText
                                v-model="filters.budget_from"
                                type="number"
                                min="0"
                                placeholder="Biudžetas nuo"
                                class="w-full"
                                size="small"
                                @input="onBudgetRangeInput"
                            />
                            <InputText
                                v-model="filters.budget_to"
                                type="number"
                                min="0"
                                placeholder="Biudžetas iki"
                                class="w-full"
                                size="small"
                                @input="onBudgetRangeInput"
                            />
                        </div>
                    </div>

                    <MultiSelect
                        v-model="filters.assigned_users"
                        :options="availableAssignedUsers"
                        optionLabel="label"
                        optionValue="code"
                        placeholder="Priskirti vartotojai"
                        class="assigned-users-filter w-full max-w-[400px]"
                        @change="filter"
                        size="small"
                    >
                        <template #option="slotProps">
                            <div class="flex flex-col">
                                <div>{{ slotProps.option.name }}</div>
                                <small v-if="slotProps.option.email" class="text-gray-500">
                                    {{ slotProps.option.email }}
                                </small>
                            </div>
                        </template>
                    </MultiSelect>

                    <div class="w-full max-w-[400px] flex items-center gap-2 min-h-[38px]">
                        <Checkbox
                            v-model="filters.imported_only"
                            inputId="imported_only_filter"
                            binary
                            @change="filter"
                        />
                        <label for="imported_only_filter" class="cursor-pointer select-none">
                            Tik importuoti kontaktai
                        </label>
                    </div>
                </div>

                <div v-if="hasActiveFilters" class="flex flex-row gap-5 justify-start flex-1 mt-4">
                    <Button
                        label="Išvalyti visus filtrus"
                        size="small"
                        class="self-start"
                        severity="warn"
                        outlined
                        @click="resetFilters"
                    />
                </div>
            </template>
        </Card>

        <Card v-if="isAdmin" class="mt-4">
            <template #content>
                <div class="flex flex-row flex-wrap items-center justify-between gap-3">
                    <div class="font-medium">
                        Pasirinkta kontaktų: {{ selectedContactsCount }}
                    </div>

                    <div class="flex flex-row items-center gap-2">
                        <Button
                            label="Eksportuoti visus kontaktus"
                            size="small"
                            icon="pi pi-file-excel"
                            @click="openExportModal('all')"
                            :loading="isExportingAllContacts"
                        />

                        <Button
                            label="Eksportuoti pasirinktus kontaktus"
                            size="small"
                            icon="pi pi-file-excel"
                            @click="openExportModal('selected')"
                            :loading="isExportingSelectedContacts"
                            :disabled="selectedContactsCount === 0"
                        />
                    </div>
                </div>
            </template>
        </Card>

        <Card
            class="mt-4"
            :pt="{
                body: () => ({
                    class: ['!p-0 !rounded-b-lg']
                })
            }"
        >
            <template #content>
                <DataTable
                    v-model:selection="tableSelection"
                    :paginator="isPaginationEnabled"
                    :rows="perPage"
                    :first="tableFirst"
                    :totalRecords="totalContactsCount"
                    :value="contacts"
                    dataKey="id"
                    :lazy="true"
                    :loading="isTableLoading"
                    tableStyle="min-width: 50rem"
                    :pt="{
                        tableContainer: () => ({
                            class: ['!rounded-xl']
                        })
                    }"
                    @page="onPageChange"
                    removableSort
                    @sort="onSort"
                    :rowClass="getRowClass"
                    sortField="created_at"
                    :sortOrder="-1"
                    :size="'small'"
                >
                    <Column
                        v-if="isAdmin"
                        selectionMode="multiple"
                        headerStyle="width: 3rem"
                    ></Column>
                    <Column field="pinned_top" header="" class="whitespace-nowrap">
                        <template #body="{ data }">
                            <i
                                v-if="data.pinned_top"
                                class="pi pi-thumbtack text-orange-500"
                                aria-label="Prisegtas viršuje"
                            ></i>
                        </template>
                    </Column>
                    <Column field="first_name" header="Vardas" :sortable="true">
                        <template #body="{ data }">
                            <div class="flex flex-row gap-1">
                                <div
                                    v-for="(item, index) in data.special_attributes"
                                    class="flex items-center"
                                >
                                    <img
                                        :src="'./assets/images/icons/' + item.icon + '.svg'"
                                        class="w-5 h-5"
                                    />
                                </div>

                                {{ data.first_name }}
                            </div>
                        </template>
                    </Column>
                    <Column field="last_name" header="Pavardė" :sortable="true"></Column>
                    <Column field="email" header="El. paštas" :sortable="true"></Column>
                    <Column
                        field="budget"
                        header="Biudžetas"
                        :sortable="true"
                        class="whitespace-nowrap"
                    ></Column>
                    <Column
                        field="region"
                        header="Regionas"
                        :sortable="true"
                        class="whitespace-nowrap"
                    ></Column>
                    <Column
                        field="phone"
                        header="Tel. nr."
                        :sortable="true"
                    >
                        <template #body="{ data }">
                            <div class="flex flex-col leading-tight">
                                <div
                                    v-for="(phoneLine, index) in getPhoneLines(data.phone)"
                                    :key="`phone-${data.id}-${index}`"
                                    class="whitespace-nowrap"
                                >
                                    {{ phoneLine }}
                                </div>
                            </div>
                        </template>
                    </Column>
                    <Column
                        field="assigned_users"
                        header="Priskirti vartotojai"
                        :sortable="true"
                        class="whitespace-nowrap"
                    >
                        <template #body="{ data }">
                            <div class="flex flex-row gap-1">
                                <div v-for="(item, index) in data.assigned_users" :key="index">
                                    <Tag :value="item.first_name" severity="info"></Tag>
                                </div>
                            </div>
                        </template>
                    </Column>
                    <Column
                        field="stage"
                        header="Stadija"
                        :sortable="true"
                        class="whitespace-nowrap"
                    ></Column>
                    <Column
                        field="contact_date"
                        header="Kreipimosi data"
                        :sortable="true"
                        class="whitespace-nowrap"
                    ></Column>
                    <Column
                        field="created_at"
                        header="Sukurta"
                        :sortable="true"
                        v-if="privilegesReady && canViewCreatedAtColumn"
                        style="white-space: nowrap"
                    ></Column>
                    <Column class="!text-end">
                        <template #body="{ data }">
                            <div class="flex flex-row justify-end gap-2">
                                <Button
                                    class=""
                                    severity="secondary"
                                    rounded
                                    @click="openContact(data.id)"
                                    :size="'small'"
                                >
                                    Atidaryti
                                </Button>
                            </div>
                        </template>
                    </Column>
                    <template #empty>Kontaktų nerasta</template>
                </DataTable>
            </template>
        </Card>

        <Dialog
            v-model:visible="isExportModalVisible"
            modal
            :style="{ width: '900px' }"
            :pt="{
                header: {
                    class: '!border-b !border-slate-200 !pb-3 !shadow-[0_2px_6px_rgba(15,23,42,0.08)]'
                },
                content: {
                    class: '!max-h-[420px] !overflow-y-auto !pr-2'
                },
                footer: {
                    class: '!border-t !border-slate-200 !pt-3 !shadow-[0_-2px_6px_rgba(15,23,42,0.08)]'
                }
            }"
        >
            <template #header>
                <div class="flex flex-col">
                    <div class="font-semibold text-[18px]">Pasirinkite eksportuojamus laukus</div>
                    <div class="text-sm text-gray-600 mt-1">
                        Pažymėkite laukus, kuriuos norite įtraukti į eksporto failą
                    </div>
                </div>
            </template>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-3 mt-1 py-3">
                <div
                    v-for="field in availableExportFields"
                    :key="field.key"
                    class="flex items-center gap-2"
                >
                    <Checkbox
                        v-model="selectedExportFields"
                        :inputId="'export_field_' + field.key"
                        :value="field.key"
                    />
                    <label :for="'export_field_' + field.key" class="cursor-pointer">
                        {{ field.label }}
                    </label>
                </div>
            </div>

            <template #footer>
                <div class="w-full flex flex-row items-center justify-between gap-2">
                    <Button
                        label="Numatytieji laukai"
                        size="small"
                        text
                        @click="resetExportFieldsToDefault"
                    />
                    <div class="flex flex-row items-center gap-2">
                        <Button
                            label="Atšaukti"
                            size="small"
                            severity="secondary"
                            outlined
                            @click="closeExportModal"
                        />
                        <Button
                            label="Eksportuoti į XLS"
                            size="small"
                            icon="pi pi-file-excel"
                            @click="confirmExport('xls')"
                            :disabled="selectedExportFields.length === 0"
                            :loading="isCurrentExportLoading && currentExportFormat === 'xls'"
                        />
                        <Button
                            label="Eksportuoti į CSV"
                            size="small"
                            icon="pi pi-file"
                            @click="confirmExport('csv')"
                            :disabled="selectedExportFields.length === 0"
                            :loading="isCurrentExportLoading && currentExportFormat === 'csv'"
                        />
                    </div>
                </div>
            </template>
        </Dialog>
    </ContentLayout>
</template>

<script setup lang="ts">
import { computed, ref, nextTick, onBeforeUnmount, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { apiRequest } from '@/services/axios'

import Tag from 'primevue/tag'
import Button from 'primevue/button'
import Card from 'primevue/card'
import Dialog from 'primevue/dialog'
import Checkbox from 'primevue/checkbox'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import InputText from 'primevue/inputtext'
import FloatLabel from 'primevue/floatlabel'
import MultiSelect from 'primevue/multiselect'

import ContentLayout from '@/components/layouts/Content.vue'
import PageTitle from '@/components/navigation/PageTitle.vue'
import { is, reloadRolesAndPermissions } from '@/extensions/permissions.ts'

type ExportScope = 'all' | 'selected'
type ExportFormat = 'xls' | 'csv'

// Refs and variables
const contacts = ref<Array<object>>([])
const totalContactsCount = ref<number>(0)
const perPage = ref<number>(0)
const isTableLoading = ref<boolean>(false)
const isPaginationEnabled = ref<boolean>(false)
const selectedContactIds = ref<Array<number>>([])
const isExportingAllContacts = ref<boolean>(false)
const isExportingSelectedContacts = ref<boolean>(false)
const isExportModalVisible = ref<boolean>(false)
const pendingExportType = ref<ExportScope | null>(null)
const currentExportFormat = ref<ExportFormat | null>(null)
const availableExportFields = ref<Array<{ key: string; label: string }>>([])
const defaultExportFields = ref<Array<string>>([])
const selectedExportFields = ref<Array<string>>([])
const router = useRouter()
const route = useRoute()
const currentPage = ref<number>(1)
const sortBy = ref()
const sortOrder = ref()
const getDefaultFilters = () => ({
    search: null,
    stages: null,
    special_attributes: null,
    regions: null,
    budget_from: null,
    budget_to: null,
    assigned_users: null,
    imported_only: false
})
const filters = ref<any>(getDefaultFilters())
const availableStages = ref()
const availableSpecialAttributes = ref()
const availableRegions = ref()
const availableAssignedUsers = ref()
const search = ref<string | null>(null)
let searchDebounceTimer: ReturnType<typeof setTimeout>
let budgetDebounceTimer: ReturnType<typeof setTimeout>
let isHydratingStateFromQuery = true
const hasActiveFilters = computed(() => {
    return (
        Boolean(filters.value.search) ||
        Boolean(filters.value.stages?.length) ||
        Boolean(filters.value.special_attributes?.length) ||
        Boolean(filters.value.regions?.length) ||
        (filters.value.budget_from !== null && filters.value.budget_from !== '') ||
        (filters.value.budget_to !== null && filters.value.budget_to !== '') ||
        Boolean(filters.value.assigned_users?.length) ||
        Boolean(filters.value.imported_only)
    )
})
const selectedContactsCount = computed(() => selectedContactIds.value.length)
const isAdmin = ref<boolean>(false)
const privilegesReady = ref<boolean>(false)
const canViewCreatedAtColumn = ref<boolean>(false)
const isCurrentExportLoading = computed(() => {
    if (pendingExportType.value === 'all') {
        return isExportingAllContacts.value
    }

    if (pendingExportType.value === 'selected') {
        return isExportingSelectedContacts.value
    }

    return false
})
const tableSelection = computed({
    get() {
        const selectedIdsSet = new Set(selectedContactIds.value)

        return contacts.value.filter((contact: any) => selectedIdsSet.has(contact.id))
    },
    set(newSelection: any[]) {
        const currentPageIds = new Set((contacts.value as any[]).map((contact: any) => contact.id))
        const selectedIdsInCurrentPage = new Set(
            (newSelection ?? [])
                .map((contact: any) => contact?.id)
                .filter((id: any) => Number.isInteger(id))
        )

        const selectedFromOtherPages = selectedContactIds.value.filter((id) => !currentPageIds.has(id))

        selectedContactIds.value = [...selectedFromOtherPages, ...Array.from(selectedIdsInCurrentPage)]
    }
})
const tableFirst = computed(() => {
    if (!perPage.value || currentPage.value <= 1) {
        return 0
    }

    return (currentPage.value - 1) * perPage.value
})

// Hooks
onMounted(async () => {
    sortBy.value = 'created_at'
    sortOrder.value = -1
    hydrateStateFromQuery()
    await nextTick()
    isHydratingStateFromQuery = false

    await refreshExportAccess()
    await Promise.all([getContacts(currentPage.value), getFiltersData()])
})

onBeforeUnmount(() => {
    clearTimeout(searchDebounceTimer)
    clearTimeout(budgetDebounceTimer)
})

watch(search, (newValue) => {
    if (isHydratingStateFromQuery) {
        return
    }

    clearTimeout(searchDebounceTimer)

    searchDebounceTimer = setTimeout(() => {
        filters.value.search = newValue

        getContacts(1)
    }, 300)
})

// Methods
async function getContacts(page: number = 1) {
    isTableLoading.value = true
    currentPage.value = page
    syncStateToQuery()

    const response = await apiRequest(
        'get',
        '/contacts',
        {},
        {
            params: {
                page: page,
                sortBy: sortBy.value,
                sortOrder: sortOrder.value,
                filters: filters.value
            }
        }
    )

    contacts.value = response.data.data.contacts.data
    totalContactsCount.value = response.data.data.contacts.total
    perPage.value = response.data.data.contacts.per_page

    if (response.data.data.contacts.last_page > 1) {
        isPaginationEnabled.value = true
    }

    isTableLoading.value = false
}

function hydrateStateFromQuery() {
    const query = route.query

    currentPage.value = getPositiveIntQueryValue(query.page, 1)
    sortBy.value = getFirstQueryValue(query.sortBy) ?? sortBy.value
    sortOrder.value = getSortOrderQueryValue(query.sortOrder, sortOrder.value)

    const restoredSearch = getFirstQueryValue(query.search)
    search.value = restoredSearch

    filters.value = {
        ...getDefaultFilters(),
        search: restoredSearch,
        stages: getArrayQueryValue(query.stages),
        special_attributes: getArrayQueryValue(query.special_attributes),
        regions: getArrayQueryValue(query.regions),
        budget_from: getSingleValueQuery(query.budget_from),
        budget_to: getSingleValueQuery(query.budget_to),
        assigned_users: getArrayQueryValue(query.assigned_users, true),
        imported_only: getBooleanQueryValue(query.imported_only)
    }
}

function syncStateToQuery() {
    const query: Record<string, any> = {}

    if (currentPage.value > 1) {
        query.page = currentPage.value
    }

    if (sortBy.value && sortBy.value !== 'created_at') {
        query.sortBy = sortBy.value
    }

    if (sortOrder.value && sortOrder.value !== -1) {
        query.sortOrder = sortOrder.value
    }

    if (filters.value.search) {
        query.search = filters.value.search
    }

    if (filters.value.stages?.length) {
        query.stages = filters.value.stages
    }

    if (filters.value.special_attributes?.length) {
        query.special_attributes = filters.value.special_attributes
    }

    if (filters.value.regions?.length) {
        query.regions = filters.value.regions
    }

    if (filters.value.budget_from !== null && filters.value.budget_from !== '') {
        query.budget_from = filters.value.budget_from
    }

    if (filters.value.budget_to !== null && filters.value.budget_to !== '') {
        query.budget_to = filters.value.budget_to
    }

    if (filters.value.assigned_users?.length) {
        query.assigned_users = filters.value.assigned_users
    }

    if (filters.value.imported_only) {
        query.imported_only = '1'
    }

    if (isQueryStateEqual(route.query, query)) {
        return
    }

    router.replace({ name: 'contacts', query })
}

function getPositiveIntQueryValue(value: unknown, fallback: number): number {
    const parsedValue = parseInt(getFirstQueryValue(value) ?? '')

    if (Number.isNaN(parsedValue) || parsedValue < 1) {
        return fallback
    }

    return parsedValue
}

function getSortOrderQueryValue(value: unknown, fallback: number): number {
    const parsedValue = parseInt(getFirstQueryValue(value) ?? '')

    if (parsedValue !== 1 && parsedValue !== -1) {
        return fallback
    }

    return parsedValue
}

function getFirstQueryValue(value: unknown): string | null {
    if (Array.isArray(value)) {
        return typeof value[0] === 'string' ? value[0] : null
    }

    if (typeof value === 'string') {
        return value
    }

    return null
}

function getSingleValueQuery(value: unknown): string | null {
    const queryValue = getFirstQueryValue(value)

    if (queryValue === null || queryValue === '') {
        return null
    }

    return queryValue
}

function getBooleanQueryValue(value: unknown): boolean {
    const queryValue = getFirstQueryValue(value)

    return queryValue === '1' || queryValue === 'true'
}

function getArrayQueryValue(
    value: unknown,
    castNumberValues: boolean = false
): Array<string | number> | null {
    let values: string[] = []

    if (Array.isArray(value)) {
        values = value.filter((queryValue): queryValue is string => typeof queryValue === 'string')
    } else if (typeof value === 'string') {
        values = [value]
    }

    const normalizedValues = values
        .map((queryValue) => queryValue.trim())
        .filter((queryValue) => queryValue.length > 0)
        .map((queryValue) => {
            if (castNumberValues && /^-?\d+$/.test(queryValue)) {
                return Number(queryValue)
            }

            return queryValue
        })

    if (!normalizedValues.length) {
        return null
    }

    return normalizedValues
}

function isQueryStateEqual(
    currentQuery: Record<string, unknown>,
    nextQuery: Record<string, unknown>
): boolean {
    const currentKeys = Object.keys(currentQuery)
    const nextKeys = Object.keys(nextQuery)

    if (currentKeys.length !== nextKeys.length) {
        return false
    }

    return currentKeys.every(
        (key) =>
            Object.prototype.hasOwnProperty.call(nextQuery, key) &&
            normalizeQueryValue(currentQuery[key]) === normalizeQueryValue(nextQuery[key])
    )
}

function normalizeQueryValue(value: unknown): string {
    if (Array.isArray(value)) {
        return value.map((item) => String(item)).join('|')
    }

    if (value === undefined || value === null) {
        return ''
    }

    return String(value)
}

async function getFiltersData() {
    const response = await apiRequest('get', '/contacts/filters-data')

    availableStages.value = response.data.data.stages
    availableSpecialAttributes.value = response.data.data.special_attributes
    availableRegions.value = response.data.data.regions
    availableAssignedUsers.value = response.data.data.assigned_users
}

async function getExportFields() {
    const response = await apiRequest('get', '/contacts/export-fields')

    availableExportFields.value = response.data.data.fields ?? []
    defaultExportFields.value = response.data.data.default_fields ?? []
    selectedExportFields.value = [...defaultExportFields.value]
}

async function refreshExportAccess() {
    await reloadRolesAndPermissions()
    isAdmin.value = is('admin')
    canViewCreatedAtColumn.value = !is('partner_collab|partner_readonly')
    privilegesReady.value = true

    if (isAdmin.value && !availableExportFields.value.length) {
        await getExportFields()
    }
}

function openContact(contactId: number) {
    router.push({
        name: 'contact',
        params: {
            id: contactId
        }
    })
}

// Events
const onPageChange = (event: any) => {
    currentPage.value = parseInt(event.page + 1)

    getContacts(currentPage.value)
}

function onSort(event: any) {
    sortBy.value = event.sortField
    sortOrder.value = event.sortOrder

    getContacts(currentPage.value)
}

function filter() {
    if (isHydratingStateFromQuery) {
        return
    }

    getContacts(1)
}

function onBudgetRangeInput() {
    if (isHydratingStateFromQuery) {
        return
    }

    clearTimeout(budgetDebounceTimer)

    budgetDebounceTimer = setTimeout(() => {
        getContacts(1)
    }, 300)
}

function resetFilters() {
    filters.value = getDefaultFilters()

    if (search.value != null) {
        search.value = null
    } else {
        getContacts(1)
    }
}

function downloadExportResponse(response: any, fallbackFileName: string, fallbackMimeType: string) {
    const contentType = response.headers['content-type'] || fallbackMimeType
    const blob = new Blob([response.data], {
        type: contentType
    })

    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url

    const contentDisposition = response.headers['content-disposition']
    const fileNameFromHeader = contentDisposition
        ? contentDisposition
              .split('filename=')
              .pop()
              ?.replaceAll('"', '')
        : null

    link.setAttribute('download', fileNameFromHeader || fallbackFileName)

    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
}

function resetExportFieldsToDefault() {
    selectedExportFields.value = [...defaultExportFields.value]
}

function closeExportModal() {
    isExportModalVisible.value = false
    pendingExportType.value = null
    currentExportFormat.value = null
}

async function openExportModal(type: ExportScope) {
    if (type === 'selected' && selectedContactsCount.value === 0) {
        return
    }

    if (!availableExportFields.value.length) {
        await getExportFields()
    }

    pendingExportType.value = type
    selectedExportFields.value = [...defaultExportFields.value]
    isExportModalVisible.value = true
}

function getMimeTypeForFormat(format: ExportFormat): string {
    if (format === 'csv') {
        return 'text/csv;charset=utf-8'
    }

    return 'application/vnd.ms-excel'
}

async function confirmExport(format: ExportFormat) {
    if (selectedExportFields.value.length === 0 || !pendingExportType.value) {
        return
    }

    currentExportFormat.value = format

    try {
        if (pendingExportType.value === 'all') {
            await exportAllContacts(selectedExportFields.value, format)
        } else {
            await exportSelectedContacts(selectedExportFields.value, format)
        }

        closeExportModal()
    } catch {
        //
    } finally {
        currentExportFormat.value = null
    }
}

async function exportAllContacts(fields: string[], format: ExportFormat) {
    isExportingAllContacts.value = true

    try {
        const response = await apiRequest(
            'post',
            '/contacts/export-all',
            {
                filters: filters.value,
                sortBy: sortBy.value,
                sortOrder: sortOrder.value,
                fields: fields,
                format: format
            },
            {
                responseType: 'blob'
            }
        )

        downloadExportResponse(
            response,
            `kontaktai_visi_${new Date().toISOString().slice(0, 10)}.${format}`,
            getMimeTypeForFormat(format)
        )
    } finally {
        isExportingAllContacts.value = false
    }
}

async function exportSelectedContacts(fields: string[], format: ExportFormat) {
    if (!selectedContactIds.value.length) {
        return
    }

    isExportingSelectedContacts.value = true

    try {
        const response = await apiRequest(
            'post',
            '/contacts/export-selected',
            {
                contact_ids: selectedContactIds.value,
                fields: fields,
                format: format
            },
            {
                responseType: 'blob'
            }
        )

        downloadExportResponse(
            response,
            `kontaktai_${new Date().toISOString().slice(0, 10)}.${format}`,
            getMimeTypeForFormat(format)
        )
    } finally {
        isExportingSelectedContacts.value = false
    }
}

const getRowClass = (item: any) => {
    return {
        '!bg-green-50': item.stage_unformatted === 'Commision set',
        '!bg-white': item.stage_unformatted === 'In progress',
        '!bg-blue-100': item.stage_unformatted === 'None',
        '!bg-orange-100': item.stage_unformatted === 'Postponed',
        '!bg-red-200': item.stage_unformatted === 'Not interested'
    }
}

function getPhoneLines(phone: unknown): string[] {
    if (phone === null || phone === undefined) {
        return []
    }

    return String(phone)
        .split('/')
        .map((part) => part.trim())
        .filter((part) => part.length > 0)
}
</script>

<style scoped>
:deep(.assigned-users-filter.p-multiselect) {
    height: 2.25rem;
    min-height: 2.25rem;
}

:deep(.assigned-users-filter .p-multiselect-label) {
    display: flex;
    align-items: center;
    line-height: 1.25rem;
    padding-top: 0.375rem;
    padding-bottom: 0.375rem;
    transform: translateY(1px);
}

:deep(.assigned-users-filter .p-multiselect-dropdown) {
    width: 2rem;
}
</style>
